import React from 'react';
import styled from 'styled-components';
import {
  faBan,
  faHandHoldingDollar,
  faPeopleGroup,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { Selector } from 'shared/design-system/components/Selector';

export enum ContributionEligibility {
  Grants = 'grants',
  Volunteers = 'volunteers',
  Both = 'both',
  None = 'none',
}

export const asContributionEligibility = (
  value: string | null | undefined,
): ContributionEligibility | undefined =>
  (Object.values(ContributionEligibility) as string[]).includes(value ?? '')
    ? (value as ContributionEligibility)
    : undefined;

export interface ContributionEligibilityOption {
  value: ContributionEligibility;
  label: string;
  icon: IconDefinition;
}

type Option = ContributionEligibilityOption;

export const CONTRIBUTION_ELIGIBILITY_OPTIONS: Option[] = [
  {
    value: ContributionEligibility.Grants,
    label: 'Donations',
    icon: faHandHoldingDollar,
  },
  {
    value: ContributionEligibility.Volunteers,
    label: 'Volunteers',
    icon: faPeopleGroup,
  },
  {
    value: ContributionEligibility.None,
    label: 'None',
    icon: faBan,
  },
];

export const CONTRIBUTION_ELIGIBILITY_QUESTION =
  'What are the contribution eligibilities for this program?';

const StyledDiv = styled.div`
  .question-label {
    margin-bottom: ${SPACERS.tiny};
  }
  .options-row {
    width: 100%;
    display: flex;
    gap: ${SPACERS.small};
  }
  .options-row .ti-sel-opt {
    flex: 1;
    margin: 0;
  }
`;

export interface ContributionEligibilitySelectorProps {
  value: ContributionEligibility | null | undefined;
  /**
   * The user picks Donations and/or Volunteers as independent checkboxes.
   * Selecting both emits `Both`; selecting neither emits `null`.
   */
  onChange: (value: ContributionEligibility | null) => void;
  disabled?: boolean;
  className?: string;
  showQuestion?: boolean;
  /** Override labels per option. */
  labels?: Partial<Record<ContributionEligibility, string>>;
}

const ContributionEligibilitySelector: React.FC<ContributionEligibilitySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className,
  showQuestion = true,
  labels,
}) => {
  const grantsSelected =
    value === ContributionEligibility.Grants ||
    value === ContributionEligibility.Both;
  const volunteersSelected =
    value === ContributionEligibility.Volunteers ||
    value === ContributionEligibility.Both;
  const noneSelected = value === ContributionEligibility.None;

  const isSelected = (opt: Option): boolean => {
    if (opt.value === ContributionEligibility.Grants) return grantsSelected;
    if (opt.value === ContributionEligibility.Volunteers)
      return volunteersSelected;
    return noneSelected;
  };

  const handleToggle = (opt: Option) => {
    if (opt.value === ContributionEligibility.None) {
      onChange(noneSelected ? null : ContributionEligibility.None);
      return;
    }

    // Toggling Donations/Volunteers clears the mutually-exclusive None state.
    const nextGrants =
      opt.value === ContributionEligibility.Grants
        ? !grantsSelected
        : grantsSelected && !noneSelected;
    const nextVolunteers =
      opt.value === ContributionEligibility.Volunteers
        ? !volunteersSelected
        : volunteersSelected && !noneSelected;

    let next: ContributionEligibility | null;
    if (nextGrants && nextVolunteers) next = ContributionEligibility.Both;
    else if (nextGrants) next = ContributionEligibility.Grants;
    else if (nextVolunteers) next = ContributionEligibility.Volunteers;
    else next = null;

    onChange(next);
  };

  return (
    <StyledDiv className={className}>
      {showQuestion && (
        <TrueImpactText variant="body-bold" className="question-label">
          {CONTRIBUTION_ELIGIBILITY_QUESTION}
        </TrueImpactText>
      )}
      <div className="options-row">
        {CONTRIBUTION_ELIGIBILITY_OPTIONS.map(option => (
          <Selector
            key={option.value}
            variant="primary-checkbox"
            icon={option.icon}
            label={labels?.[option.value] ?? option.label}
            selected={isSelected(option)}
            disabled={disabled}
            margin="0"
            padding={`${SPACERS.tiny} ${SPACERS.small}`}
            maxWidth="100%"
            onClick={() => handleToggle(option)}
          />
        ))}
      </div>
    </StyledDiv>
  );
};

export default ContributionEligibilitySelector;
